import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { ServerExceptionCode, fmtMsg } from "commonui";
import { Component, Ref, Vue } from "vue-property-decorator";
import { AuditHistoryLocale } from "@/locales/localeid";
import { AuditHistoryModel, AuditHistoryResponseModel, AuditHistoryService, IAuditHistoryService } from "@/services/audit-history";
import { getInnerHtmlFromText, getRouteAccessLevel, stripAnchorTags, stripHtmlTags } from "@/util/func";
import { AccessLevel, MediaPlayerContentType, MediaSlot, MediaType } from "@/util/enum";
import { IWorkbookCell } from "wijmo/wijmo.xlsx";
import moment from "moment";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import { cloneDeep, debounce, isString } from "lodash";
import IPdfPagesViewer from "@/components/pdf-pages-viewer/pdf-pages-viewer";
import PdfPagesViewer from "@/components/pdf-pages-viewer/pdf-pages-viewer.vue";
import VideoJSPlayer from "@/components/videojsplayer/videojsplayer.vue";
import IVideoJSPlayer from "@/components/videojsplayer/videojsplayer";
import { ResourceService } from "@/services/resource";
import ImageViewerModal from "@/components/image-viewer-modal/image-viewer-modal.vue";
import { ImageViewerModalRef } from "@/components/image-viewer-modal";

interface MediaItem {
	mediaSlotId: number;
	mediaId: string;
	mediaTypeId: number;
	isPage: boolean;
	isShowAdditionalData: boolean;
}

@Component({
	components: {
		Grid,
		VideoJSPlayer,
		PdfPagesViewer,
		ImageViewerModal
	}
})
export default class AuditHistoryManager extends Vue {
	public readonly locale = AuditHistoryLocale;
	auditHistoryService: IAuditHistoryService = AuditHistoryService();
	public gridLoading: boolean = false;
	public gridData: AuditHistoryModel[] = [];
	public totalGridData: number = 1;
	public pageImageSrc = "";
	public pageImageMetaData: string | undefined = "";
	public pageSize = 25;
	public currentPage: number = 1;
	accessLevel = AccessLevel;
	@Ref() readonly gridCompRef!: GridRef;
	@Ref("pdfPagesViewerRef") pdfPagesViewerRef!: IPdfPagesViewer;
	@Ref("videoPlayerRef") videoPlayerRef!: IVideoJSPlayer;
	$refs!: {
		liteViewer: ImageViewerModalRef;
	};

	mounted() {
		const grid = document.getElementById("grid");
		if (grid) {
			grid.addEventListener("click", this.extractMediaInfoAndView);
		}
	}

	beforeDestroy() {
		const grid = document.getElementById("grid");
		if (grid) {
			grid.removeEventListener("click", this.extractMediaInfoAndView);
		}
	}

	extractMediaInfoAndView(event) {
		event.stopPropagation();
		event.preventDefault();

		if (event.target && event.target instanceof HTMLElement && event.target.tagName.toLowerCase() === "a") {
			const mediaId = event.target.getAttribute("data-id");
			const mediaTypeId = event.target.getAttribute("mediatypeid")
				? Number(event.target.getAttribute("mediatypeid"))
				: null;
			const mediaSlotId = event.target.getAttribute("mediaslotid")
				? Number(event.target.getAttribute("mediaslotid"))
				: null;
			const isPage = event.target.getAttribute("isPage");
			const metadata = event.target.getAttribute("metadata");
			if (isPage) {
				ResourceService.getDownloadPageUrl(mediaId).then((res) => {
					this.pageImageSrc = res;
					this.pageImageMetaData = cloneDeep(JSON.parse(metadata));
					this.$refs.liteViewer.open();
				});
			} else {
				if (mediaId && mediaTypeId && mediaSlotId) {
					this.viewMedia({
						mediaSlotId: mediaSlotId,
						mediaId: mediaId,
						mediaTypeId: mediaTypeId,
						isPage: false,
						isShowAdditionalData: false
					});
				}
			}
		}
	}

	onLiteViewerClose() {
		this.pageImageSrc = "";
		this.pageImageMetaData = undefined;
	}

	public async viewMedia(item: MediaItem) {
		if (item.mediaSlotId === MediaSlot.Audio || item.mediaSlotId === MediaSlot.IndividualAudio) {
			try {
				const fileUrl = await ResourceService.getDownloadMediaUrl(item.mediaId);
				if (isString(fileUrl) && item.mediaId) {
					const result = this.videoPlayerRef.showModal(item.mediaId as string, MediaType.MP3, "Media Viewer");
					if (result) {
						setTimeout(() => {
							this.videoPlayerRef.setAWSMediaPlayer(
								item.mediaId as string,
								fileUrl,
								MediaPlayerContentType.mp3
							);
						}, 100);
					}
				}
			} catch (err) {
				if (err && err["error_code"] === ServerExceptionCode.NotFound) {
					this.videoPlayerRef.showModal(
						item.mediaId as string,
						MediaType.MP3,
						"Media Viewer",
						[],
						this.formatMessage(this.locale.MediaNotFoundText)
					);
				}
			}
		} else {
			if (item.mediaId) {
				this.pdfPagesViewerRef.open({
					mediaId: item.mediaId,
					mediaSlotId: item.mediaSlotId,
					isPage: item.isPage,
					isShowAdditionalData: item.isShowAdditionalData
				});
			}
		}
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	created() {
		this.getAuditTrailLog(this.currentPage, this.pageSize);
	}

	onPageChange(pageNumber: number) {
		if (this.currentPage !== pageNumber) {
			this.debouncedGetAuditTrailLog(pageNumber, this.pageSize);
			this.currentPage = pageNumber;
		}
	}

	debouncedGetAuditTrailLog = debounce((pageNumber, pageSize) => {
		this.getAuditTrailLog(pageNumber, pageSize);
	}, 200);

	getAuditTrailLog(pageNumber: number, pageSize: number) {
		this.gridLoading = true;
		this.auditHistoryService
			.getAuditHistory(pageNumber, pageSize)
			.then((res: AuditHistoryResponseModel) => {
				res.items.forEach((currentLog) => {
					currentLog.timeOfSave = moment(currentLog.timeOfSave).format("YYYY/MM/DD");
				});
				this.gridData = res.items;
				this.totalGridData = res.totalCount;
				this.gridLoading = false;
			})
			.catch((err) => {
				this.gridLoading = false;
			});
	}

	get exportOptions() {
		return {
			filename: fmtMsg(AuditHistoryLocale.AuditHistoryExportExcelFileName),
			formatCell: (cell: IWorkbookCell, binding: string) => {
				if (cell.value && (binding == "remark" || binding == "title")) {
					cell.value = getInnerHtmlFromText(cell.value);
				}
			}
		};
	}

	getTootTipWithoutHtmlTags(inputString: string) {
		return getInnerHtmlFromText(inputString);
	}

	getAllAuditTrailLogs() {
		return this.auditHistoryService.getAllAuditHistory();
	}
}
